/**
 * BundlePersonalizationView — Bundle-aware view for the Personalization widget.
 *
 * Shows per-child-item template summaries with shared field toggles.
 * Shared fields = customer fills once, value applies to all bundle items.
 *
 * Plan: 117-personalization-admin-ui, Epic 04
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Package, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TemplateStatusBadge } from "./TemplateStatusBadge";
import { TemplateTranslateButton } from "./TemplateTranslateButton";
import { getFieldTypeIcon } from "./FieldTypeRadioCards";
import { useSaveSharedFields } from "../api/customizationAdminHooks";
import type { BundleCustomizationConfig, BundleChildItem } from "../api/customizationAdminTypes";

interface BundlePersonalizationViewProps {
  config: BundleCustomizationConfig;
  itemCode: string;
}

export function BundlePersonalizationView({
  config,
  itemCode: _itemCode,
}: BundlePersonalizationViewProps) {
  const items = config.items || [];
  const customizableCount = items.filter((i) => i.template_name).length;

  return (
    <div className="rounded-2xl bg-card shadow-apple overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-2">
        <Package className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          Bundle with {items.length} item{items.length !== 1 ? "s" : ""}
          {customizableCount > 0 && (
            <> — {customizableCount} customizable</>
          )}
        </span>
      </div>

      <div className="mx-5 border-t border-border/40" />

      {/* Child item sections */}
      {items.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-muted-foreground">
          No items in this bundle.
        </div>
      ) : (
        <div className="divide-y divide-border/40">
          {items.map((item) => (
            <BundleChildItemSection key={item.row_name} item={item} />
          ))}
        </div>
      )}

      {/* Info footer */}
      {customizableCount > 0 && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="px-5 py-3 flex items-start gap-2">
            <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Shared fields are filled once by the customer and applied to all
              items in the bundle.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Child Item Section ──────────────────────────────────────

function BundleChildItemSection({ item }: { item: BundleChildItem }) {
  const navigate = useNavigate();
  const saveSharedFields = useSaveSharedFields();

  const handleToggleShared = useCallback(
    (fieldLabel: string, currentlyShared: boolean) => {
      const currentLabels = item.shared_customization_fields || [];
      let newLabels: string[];

      if (currentlyShared) {
        // Remove — case-insensitive match
        const lowerLabel = fieldLabel.toLowerCase();
        newLabels = currentLabels.filter(
          (l) => l.toLowerCase() !== lowerLabel,
        );
      } else {
        // Add — use original case from the field
        newLabels = [...currentLabels, fieldLabel];
      }

      saveSharedFields.mutate(
        {
          bundleItemRowName: item.row_name,
          sharedFields: newLabels,
        },
        {
          onSuccess: () => {
            toast.success(
              currentlyShared
                ? `"${fieldLabel}" is no longer shared`
                : `"${fieldLabel}" is now shared across bundle`,
            );
          },
        },
      );
    },
    [item.row_name, item.shared_customization_fields, saveSharedFields],
  );

  // No template linked
  if (!item.template_name) {
    return (
      <div className="px-5 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium truncate">{item.item_name}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          No customization template linked.{" "}
          <button
            type="button"
            className="text-sky-600 hover:underline"
            onClick={() =>
              navigate(`/inventory/item/${encodeURIComponent(item.item_code)}`)
            }
          >
            View item
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-4">
      {/* Item header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium truncate">{item.item_name}</span>
        {item.template_status && (
          <TemplateStatusBadge status={item.template_status} />
        )}
      </div>

      {/* Template name + Draft warning + Translate */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-muted-foreground truncate">
          {item.template_display_name}
        </span>
        {item.template_status === "Draft" && (
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            Not visible on storefront
          </span>
        )}
        <TemplateTranslateButton
          className="ml-auto shrink-0"
          templateDocname={item.template_name || ""}
          templateDisplayName={item.template_display_name || ""}
          fields={item.fields}
        />
      </div>

      {/* Fields with shared toggles */}
      {item.fields.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {item.fields.map((f) => {
            const Icon = getFieldTypeIcon(f.field_type);
            return (
              <div
                key={f.label}
                className="flex items-center gap-2 text-sm"
              >
                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{f.label}</span>
                <span className="text-xs text-muted-foreground">
                  {f.field_type}
                </span>
                {f.required === 1 && (
                  <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-md">
                    Required
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] text-muted-foreground">
                    Shared
                  </span>
                  <Switch
                    checked={f.shared}
                    onCheckedChange={() =>
                      handleToggleShared(f.label, f.shared)
                    }
                    className="scale-75"
                    disabled={saveSharedFields.isPending}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mb-3">
          No fields in this template.
        </p>
      )}

      {/* Edit template link */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={() =>
          navigate(
            `/inventory/customization-templates/${encodeURIComponent(item.template_name!)}`,
          )
        }
      >
        Edit Template
        <ExternalLink className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}
